/**
 * Keeps the main product price (and the "pay in 4" amount) in sync with the selected offer card.
 * - "1 nail pen": the product's own price.
 * - bundle (3 + 2 free): the bundle price and its struck-through price from the offer card.
 * Display only: the cart is not changed by this script.
 */

const CARD = '.product-details .offer-card';

/** @param {string} text */
function parseAmount(text) {
  const match = text.replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
  return match ? parseFloat(match[1]) : NaN;
}

/**
 * Formats an amount with the same currency symbol/position as the sample text.
 * @param {number} amount
 * @param {string} sample
 */
function formatLike(amount, sample) {
  const symbol = sample.replace(/[\d.,\s]/g, '');
  const number = amount.toFixed(2);
  return sample.trim().startsWith(symbol) ? `${symbol}${number}` : `${number}${symbol}`;
}

/**
 * Writes the price values of the chosen offer into a price container.
 * @param {Element} container
 * @param {boolean} bundle
 * @param {string} bundlePrice
 * @param {string} bundleCompare
 */
function writePrices(container, bundle, bundlePrice, bundleCompare) {
  const priceEl = container.querySelector('.price-item__group.price:not(.compare-at-price) bdi, :scope > .price bdi, .price bdi');
  const compareEl = container.querySelector('.compare-at-price bdi');
  for (const el of [priceEl, compareEl]) {
    if (el && el.dataset.original === undefined) el.dataset.original = el.textContent ?? '';
  }

  if (priceEl && priceEl.textContent !== (bundle ? bundlePrice : priceEl.dataset.original)) {
    priceEl.textContent = bundle ? bundlePrice : (priceEl.dataset.original ?? '');
  }
  if (compareEl) {
    const wanted = bundle ? bundleCompare : (compareEl.dataset.original ?? '');
    if (compareEl.textContent !== wanted) compareEl.textContent = wanted;
    const group = compareEl.closest('.compare-at-price');
    if (group instanceof HTMLElement) group.hidden = !(compareEl.textContent ?? '').trim();
  }
}

/** @type {{ bundle: boolean, price: string, compare: string } | null} */
let lastOffer = null;

/** The sticky bar follows the chosen offer too: its price and its title. */
function applySticky(bundle, bundlePrice, bundleCompare) {
  lastOffer = { bundle, price: bundlePrice, compare: bundleCompare };
  document.querySelectorAll('.sticky-add-to-cart__price').forEach((container) => {
    writePrices(container, bundle, bundlePrice, bundleCompare);
  });

  document.querySelectorAll('.sticky-add-to-cart__title').forEach((title) => {
    if (!(title instanceof HTMLElement)) return;
    if (title.dataset.original === undefined) title.dataset.original = (title.textContent ?? '').trim();
    const wanted = bundle ? (title.dataset.bundleTitle ?? '3 nail pens + 2 free') : title.dataset.original;
    if ((title.textContent ?? '').trim() !== wanted) title.textContent = wanted;
  });
}

/** @param {Element} card */
function apply(card) {
  const container = card.closest('[ref="priceContainer"]');
  if (!container) return;

  const priceEl = container.querySelector('.price-item__group.price:not(.compare-at-price) bdi, :scope > .price bdi');
  const compareEl = container.querySelector('.compare-at-price bdi');
  const installmentEl = container.querySelector('.price-installments strong');
  const checked = card.querySelector('input[type="radio"]:checked');
  if (!(checked instanceof HTMLInputElement)) return;

  // Remember the product's own values the first time we touch them.
  for (const el of [priceEl, compareEl, installmentEl]) {
    if (el && el.dataset.original === undefined) el.dataset.original = el.textContent ?? '';
  }

  const bundle = checked.value !== '1';
  const bundlePrice = card.querySelector('.offer-card__bundle .offer-card__price')?.textContent?.trim() ?? '';
  const bundleCompare = card.querySelector('.offer-card__bundle .offer-card__compare')?.textContent?.trim() ?? '';

  applySticky(bundle, bundlePrice, bundleCompare);

  if (priceEl) priceEl.textContent = bundle ? bundlePrice : (priceEl.dataset.original ?? '');
  if (compareEl) compareEl.textContent = bundle ? bundleCompare : (compareEl.dataset.original ?? '');

  if (installmentEl) {
    const quarter = parseAmount(bundlePrice) / 4;
    installmentEl.textContent =
      bundle && Number.isFinite(quarter) ? formatLike(quarter, bundlePrice) : (installmentEl.dataset.original ?? '');
  }

  // A bundle price without a compare price would leave an empty struck-through element behind.
  if (compareEl) {
    const group = compareEl.closest('.compare-at-price');
    if (group instanceof HTMLElement) group.hidden = !(compareEl.textContent ?? '').trim();
  }
}

function applyAll() {
  document.querySelectorAll(CARD).forEach(apply);
}

document.addEventListener('change', (event) => {
  const target = event.target;
  if (target instanceof HTMLInputElement && target.type === 'radio' && target.closest(CARD)) {
    const card = target.closest(CARD);
    if (card) apply(card);
  }
});

// Initial state (the bundle is selected by default) and after the shade switcher swaps the page.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', applyAll);
} else {
  applyAll();
}

document.addEventListener('shade-switcher:updated', applyAll);

// The sticky bar re-renders its price now and then (variant changes): apply the chosen offer again.
// Only the sticky bar is watched and only changed text is written, so this cannot trigger itself.
let pendingSticky = false;
const stickyObserver = new MutationObserver(() => {
  if (pendingSticky || !lastOffer) return;
  pendingSticky = true;
  requestAnimationFrame(() => {
    pendingSticky = false;
    applySticky(lastOffer.bundle, lastOffer.price, lastOffer.compare);
  });
});

function watchSticky() {
  const sticky = document.querySelector('sticky-add-to-cart');
  if (sticky) stickyObserver.observe(sticky, { childList: true, subtree: true, characterData: true });
}

watchSticky();
document.addEventListener('shade-switcher:updated', watchSticky);
